import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";

const API_URL = `${API_BASE_URL}/api`;

const DepartmentService = {
  getDepartments: async (companyId, token, userType) => {
    try {
      const response = await axios.get(`${API_URL}/departments`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },
  addDepartment: async (department, companyId, token, userType) => {
    try {
      const response = await axios.post(`${API_URL}/departments`, department, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  },
  deleteDepartment: async (id, companyId, token, userType) => {
    try {
      await axios.delete(`${API_URL}/departments/${id}`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
    } catch (error) {
      console.error("Error deleting department:", error);
      throw error;
    }
  }
};

const DepartmentForm = ({ onDepartmentAdded, companyId, token, userType }) => {
  const [form, setForm] = useState({ dname: "", description: "" });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await DepartmentService.addDepartment(form, companyId, token, userType);
      setForm({ dname: "", description: "" });
      setSuccess("Department added successfully!");
      setError(null);
      onDepartmentAdded();
    } catch (error) {
      setError(error.response?.data?.error || "Failed to add department");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white p-6 rounded-xl shadow-lg mb-6 max-w-4xl mx-auto"
    >
      <h3 className="text-xl font-bold text-gray-700 mb-4">Add Department</h3>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-600 p-4 rounded-lg mb-4">
          {success}
        </div>
      )}
      
      <input
        name="dname"
        placeholder="Name"
        value={form.dname}
        onChange={handleChange}
        required
        className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <textarea
        name="description"
        placeholder="Description"
        value={form.description}
        onChange={handleChange}
        required
        className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <button
        type="submit"
        className="w-full bg-blue-600 text-white font-bold p-3 rounded-lg hover:bg-blue-700"
      >
        Add Department
      </button>
    </form>
  );
};

const DepartmentList = ({ departments, onDeleteDepartment }) => (
  <div className="bg-white p-6 rounded-xl shadow-lg max-w-4xl mx-auto">
    <h3 className="text-xl font-bold text-gray-700 mb-4">Departments</h3>
    <div className="w-full overflow-auto max-h-[70vh]">
      <table className="w-full min-w-max border-collapse bg-white rounded-lg shadow-md overflow-hidden">
        <thead>
          <tr className="bg-blue-600 text-white">
            <th className="p-3 text-left font-semibold">Name</th>
            <th className="p-3 text-left font-semibold">Description</th>
            <th className="p-3 text-left font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody>
          {departments.map((department) => (
            <tr
              key={department.id}
              className="odd:bg-gray-50 even:bg-gray-100 hover:bg-gray-200"
            >
              <td className="p-3 border-b border-gray-200">
                {department.dname}
              </td>
              <td className="p-3 border-b border-gray-200">
                {department.description}
              </td>
              <td className="p-3 border-b border-gray-200">
                <button
                  onClick={() => onDeleteDepartment(department.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const DepartmentsPage = () => {
  const { user, userType, companyId, logout } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDepartments = async () => {
    if (!companyId) {
      logout();
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const data = await DepartmentService.getDepartments(companyId, token, userType);
      setDepartments(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching departments:", err);
      setError("Failed to fetch departments. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDepartment = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await DepartmentService.deleteDepartment(id, companyId, token, userType);
      setDepartments((prevDepartments) =>
        prevDepartments.filter((department) => department.id !== id)
      );
      setError(null);
    } catch (err) {
      console.error("Error deleting department:", err);
      setError("Failed to delete department. Please try again later.");
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, [companyId]);

  return (
    <div className="w-full p-6 space-y-6 bg-gradient-to-r from-blue-900 to-blue-700 min-h-screen">
      <h1 className="text-4xl font-bold text-white mb-6 text-center">Department Management</h1>
      
      {companyId ? (
        <>
          <DepartmentForm 
            onDepartmentAdded={fetchDepartments} 
            companyId={companyId}
            token={localStorage.getItem('token')}
            userType={userType}
          />
          
          {loading ? (
            <div className="text-center text-lg text-white">Loading...</div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg max-w-4xl mx-auto">
              {error}
            </div>
          ) : (
            <DepartmentList
              departments={departments}
              onDeleteDepartment={handleDeleteDepartment}
            />
          )}
        </>
      ) : (
        <div className="text-center text-lg text-white">
          Company information not available. Please login again.
        </div>
      )}
    </div>
  );
};

export default DepartmentsPage;